<script setup lang="ts">
import InputNumber from '@/components/BaseComponents/InputNumber.vue';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import type { CreateMenuCategoryRequest, MenuCategory, UpdateMenuCategoryRequest } from '@/types/menu';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();
const confirm = useConfirm();

interface Props {
  visible: boolean;
  category?: MenuCategory | null;
  totalCategories?: number;
}

interface Emits {
  (e: 'update:visible', value: boolean): void;
  (e: 'save', data: CreateMenuCategoryRequest | UpdateMenuCategoryRequest): void;
  (e: 'delete', categoryId: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  category: null,
  totalCategories: 0,
});

const emit = defineEmits<Emits>();

const form = ref<(CreateMenuCategoryRequest | UpdateMenuCategoryRequest) & { id?: number }>({
  name: '',
  order: 1,
});

const isEditing = computed(() => !!props.category);
const dialogTitle = computed(() => (isEditing.value ? t('menu.category.update.label') : t('menu.category.add.label')));

const canSave = computed(() => form.value.name.trim());

// Helper function to get the display name (translated or raw)
const getDisplayName = (category: MenuCategory) => {
  const translationKey = `menu.categories.${category.name}`;
  return te(translationKey) ? t(translationKey) : category.name;
};

// Watch for category changes to populate form
watch(
  () => props.category,
  (newCategory) => {
    if (newCategory) {
      form.value = {
        id: newCategory.id,
        name: getDisplayName(newCategory),
        order: newCategory.order || 1,
      };
    } else {
      // New category - set order to be the next position
      form.value = {
        name: '',
        order: props.totalCategories + 1,
      };
    }
  },
  { immediate: true },
);

// Watch for totalCategories changes to update default order for new categories
watch(
  () => props.totalCategories,
  (newTotal) => {
    if (!props.category) {
      // Only update order for new categories
      form.value.order = newTotal + 1;
    }
  },
);

// Reset form when dialog closes
watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible && !props.category) {
      // Only reset for new categories when dialog opens
      form.value = {
        name: '',
        order: props.totalCategories + 1,
      };
    }
  },
);

// Add computed property for max allowed order
const maxAllowedOrder = computed(() => {
  if (isEditing.value) {
    // When editing, max is current total (since we're replacing one position)
    return props.totalCategories;
  } else {
    // When creating, max is total + 1 (new position)
    return props.totalCategories + 1;
  }
});

// Watch for order changes and auto-correct if needed
watch(
  () => form.value.order,
  (newOrder) => {
    if (newOrder && newOrder > maxAllowedOrder.value) {
      form.value.order = maxAllowedOrder.value;
    }
  },
);

const handleSave = () => {
  if (!canSave.value) return;

  if (isEditing.value) {
    const { id: _id, ...updateData } = form.value;
    emit('save', updateData);
  } else {
    emit('save', form.value);
  }
};

const handleDelete = () => {
  if (!props.category || !can('menus', 'delete')) return;

  confirm.require({
    message: `${t('menu.category.delete.confirm')} "${props.category.name}"?`,
    header: t('menu.category.delete.confirm'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: () => {
      if (props.category) {
        emit('delete', props.category.id);
      }
    },
  });
};

const handleCancel = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :header="dialogTitle"
    :style="{ width: '25rem', maxWidth: '90vw' }"
    @update:visible="emit('update:visible', $event)"
  >
    <div v-if="visible" class="mb-4 flex items-center gap-4">
      <label for="categoryName" class="w-24 font-semibold">{{ t('menu.category.label') }}</label>
      <InputText id="categoryName" v-model="form.name" class="flex-auto" autocomplete="off" />
    </div>

    <div class="mb-4 flex items-center gap-4">
      <label for="categoryOrder" class="w-24 font-semibold">{{ t('common.order') }}</label>
      <InputNumber id="categoryOrder" v-model="form.order" class="flex-auto" :min="1" :max="maxAllowedOrder" :use-grouping="false" />
    </div>

    <div class="mb-4 flex flex-col gap-1">
      <small class="text-gray-600">
        {{ t('menu.category.position.help') }}
      </small>
      <small>
        {{
          isEditing
            ? t('menu.category.position.helpEdit', { max: maxAllowedOrder })
            : t('menu.category.position.helpCreate', { max: maxAllowedOrder })
        }}
      </small>
    </div>

    <div class="flex justify-between gap-2">
      <Button v-if="isEditing && can('menus', 'delete')" type="button" :label="t('common.delete')" severity="danger" outlined @click="handleDelete" />
      <div class="flex justify-end gap-2" :class="{ 'ml-auto': !isEditing || !can('menus', 'delete') }">
        <Button type="button" :label="t('common.cancel')" outlined @click="handleCancel" />
        <Button type="button" :label="t('common.save')" :disabled="!canSave" @click="handleSave" />
      </div>
    </div>
  </Dialog>
</template>
